import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Box, Button, Flex, Icon, RadioGroup, Text } from "@telia/teddy";
import _partition from "lodash/partition";
import _values from "lodash/values";
import { useDispatch, useSelector } from "react-redux";
import { MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS, NEW_ACCOUNT_VALUE } from "../../../../../../constants/app";
import { CART_ITEM_SUBSCRIPTION_TYPES, CUSTOMER_TYPE } from "../../../../../../constants";
import { RootState } from "../../../../../../models";
import {
    ChangeScope,
    CustomerExistingAccount,
    CustomerExistingSubscription,
} from "../../../../../../models/state/retailer";
import { getSubscriptions } from "../../../../checkout/state/selectors/subscriptionsSelectors";
import { getActiveSubscription, getAllItemsOfType } from "../../../../checkout/state/selectors/cartSelectors";
import { setSelectedAccountId } from "../../../state/actions/customerActions";
import {
    getCustomerExistingAccounts,
    getCustomerSelectedAccountId,
    getReopenExistingSubscriptionPhone,
} from "../../../state/selectors/customerSelectors";
import { setActiveSubscription } from "../../../../checkout/state/actions/cartActions";
import { isCountableSubscription, isSubscriptionActive } from "../../../utils";
import ExistingSubscriptionCard from "../ExistingSubscriptionCard/ExistingSubscriptionCard";
import ExistingSubscriptionNewAccountConflictModal from "../ExistingSubscriptionNewAccountConflictModal/ExistingSubscriptionNewAccountConflictModal";
import "./AccountSelector.less";
import {
    clearReopenExistingSubscriptionPhone,
    setExistingSubscriptionForChange,
    setSubscriptionStep,
} from "../../../state/actions/subscriptionStepsActions";
import { isNonMhsSubscription } from "../../../../../../shared/subscriptionCodeUtils";
import { getAccountFamilyDiscounts } from "../../organisms/ChooseSubscription/familyDiscount";

interface AccountTreeProps {
    account: CustomerExistingAccount;
    isFull: boolean;
    isDisabled: boolean;
    isSelected: boolean;
    isSelectedAccountFull: boolean;
    isSelectedAccountNaturallyFull?: boolean;
    showRadio: boolean;
    defaultOpen?: boolean;
    showHeader?: boolean;
}

const AccountTree: React.FC<AccountTreeProps> = ({
    account,
    isFull,
    isDisabled,
    isSelected,
    isSelectedAccountFull,
    isSelectedAccountNaturallyFull = false,
    showRadio,
    defaultOpen = false,
    showHeader = true,
}) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const subscriptionParam = searchParams.get("subscription");
    const reopenSubscriptionPhone = useSelector((state: RootState) => getReopenExistingSubscriptionPhone(state));
    const hasMatchingSubscription =
        !!subscriptionParam && account.subscriptions.some((sub) => sub.phoneNumber === subscriptionParam);
    const hasReopenSubscription =
        !!reopenSubscriptionPhone && account.subscriptions.some((sub) => sub.phoneNumber === reopenSubscriptionPhone);
    const [isOpen, setIsOpen] = useState(defaultOpen || hasMatchingSubscription || hasReopenSubscription);
    const [showChangeBlockedModal, setShowChangeBlockedModal] = useState(false);
    const selectedAccountId = useSelector((state: RootState) => getCustomerSelectedAccountId(state));
    const dispatch = useDispatch();
    let initialSelectedSubscription: string | undefined;
    if (hasMatchingSubscription) {
        initialSelectedSubscription = subscriptionParam ?? undefined;
    } else if (hasReopenSubscription) {
        initialSelectedSubscription = reopenSubscriptionPhone;
    }
    const [selectedSubscription, setSelectedSubscription] = useState(initialSelectedSubscription);

    useEffect(() => {
        if (hasMatchingSubscription) {
            setIsOpen(true);
            setSelectedSubscription(subscriptionParam ?? undefined);
            navigate({ search: "" }, { replace: true });
        }
    }, [hasMatchingSubscription, navigate, subscriptionParam]);

    useEffect(() => {
        if (hasReopenSubscription) {
            setIsOpen(true);
            setSelectedSubscription(reopenSubscriptionPhone);
            dispatch(clearReopenExistingSubscriptionPhone());
        }
    }, [dispatch, hasReopenSubscription, reopenSubscriptionPhone]);
    const activeSubscription = useSelector((state: RootState) => getActiveSubscription(state));

    const cartSubscriptionItems = useSelector((state: RootState) =>
        getAllItemsOfType(state, ...CART_ITEM_SUBSCRIPTION_TYPES),
    );

    const cartSubscriptions = useSelector((state: RootState) => getSubscriptions(state));

    const cartSubscriptionByPhone = useMemo(
        () =>
            Object.fromEntries(
                _values(cartSubscriptions)
                    .filter(
                        (sub): sub is typeof sub & { phoneNumber: NonNullable<typeof sub.phoneNumber> } =>
                            !!sub.phoneNumber?.selectedPhoneNumber,
                    )
                    .map((sub) => [sub.phoneNumber.selectedPhoneNumber, sub]),
            ),
        [cartSubscriptions],
    );

    const phoneNumbersInCart = useMemo(() => new Set(Object.keys(cartSubscriptionByPhone)), [cartSubscriptionByPhone]);

    const cartSubscriptionItemByBundleId = useMemo(
        () =>
            Object.fromEntries(
                cartSubscriptionItems
                    .filter((item): item is typeof item & { bundleId: string } => !!item.bundleId)
                    .map((item) => [item.bundleId, item]),
            ),
        [cartSubscriptionItems],
    );

    const [activeSubscriptions, inactiveSubscriptions] = useMemo(() => {
        return _partition([...account.subscriptions], (sub) => isSubscriptionActive(sub));
    }, [account.subscriptions]);

    const cartPhoneNumbersToOfferingCode = useMemo(() => {
        const entries = Object.entries(cartSubscriptionByPhone).filter(([, sub]) => {
            const targetAccountId = sub.phoneNumber.accountId;
            return targetAccountId ? targetAccountId === account.accountId : isSelected;
        });
        return entries.length > 0 ? Object.fromEntries(entries.map(([phone, sub]) => [phone, sub.code])) : undefined;
    }, [cartSubscriptionByPhone, isSelected, account.accountId]);

    const familyDiscounts = useMemo(() => {
        const cartCodes = _values(cartSubscriptions)
            .filter((sub) => {
                const targetAccountId = sub.phoneNumber?.accountId;
                return targetAccountId ? targetAccountId === account.accountId : isSelected;
            })
            .map((sub) => sub.code)
            .filter(Boolean) as string[];
        return getAccountFamilyDiscounts(activeSubscriptions, cartCodes, cartPhoneNumbersToOfferingCode);
    }, [activeSubscriptions, cartSubscriptions, isSelected, account.accountId, cartPhoneNumbersToOfferingCode]);

    if (
        account.accountId === NEW_ACCOUNT_VALUE ||
        account.subscriptions.length === 0 ||
        account.accountId === undefined
    ) {
        return null;
    }

    const changeBlockedModal = (
        <ExistingSubscriptionNewAccountConflictModal
            open={showChangeBlockedModal}
            onClose={() => setShowChangeBlockedModal(false)}
            title="Kan ikke endre eksisterende abonnement"
            message="Endringer på eksisterende abonnement kan ikke gjøres når ny avtale er valgt. Velg et eksisterende fakturanummer for å gjøre endringer på abonnementet."
        />
    );

    const handleSelect = (id: string) => {
        if (selectedSubscription === id) {
            setSelectedSubscription(undefined);
        } else {
            setSelectedSubscription(id);
        }
    };

    const handleChangeSubscription = (subscription: CustomerExistingSubscription, scope: ChangeScope = "full") => {
        if (selectedAccountId === NEW_ACCOUNT_VALUE) {
            setShowChangeBlockedModal(true);
            return;
        }

        if (selectedAccountId !== account.accountId) {
            dispatch(setSelectedAccountId(account.accountId));
        }
        dispatch(
            setExistingSubscriptionForChange(scope === "full" ? subscription : { ...subscription, changeScope: scope }),
        );
        const existingCartSub = cartSubscriptionByPhone[subscription.phoneNumber];
        if ((scope === "full" || scope === "subscriptionChange") && existingCartSub?.id) {
            dispatch(setActiveSubscription(existingCartSub.id));
            dispatch(setSubscriptionStep(existingCartSub.id, 0));
        }
    };

    const subscriptionContent = (
        <Box className="account-selector__subscriptions" ml={showHeader ? "200" : undefined}>
            {activeSubscriptions.map((sub, i) => (
                <ExistingSubscriptionCard
                    key={sub.phoneNumber}
                    sub={sub}
                    selectedSubscription={selectedSubscription}
                    handleSelect={handleSelect}
                    handleChangeSubscription={handleChangeSubscription}
                    subscriptionInfo={sub}
                    familyDiscount={familyDiscounts[i]}
                    isChanged={phoneNumbersInCart.has(sub.phoneNumber)}
                    changedSubscription={cartSubscriptionByPhone[sub.phoneNumber]}
                    changedSubscriptionCartItem={
                        cartSubscriptionItemByBundleId[cartSubscriptionByPhone[sub.phoneNumber]?.id || ""]
                    }
                    isCurrentlyAddingSubscription={!!activeSubscription}
                    isChangeBlockedByAccountFull={isSelectedAccountFull && !isSelected}
                    isBlockedDueToNaturallyFullAccount={isSelectedAccountNaturallyFull && !isSelected}
                />
            ))}
            {inactiveSubscriptions.length > 0 && (
                <Text as="p" variant="additional-100">
                    <i>
                        Det er {inactiveSubscriptions.length}{" "}
                        {inactiveSubscriptions.length === 1 ? "inaktivt abonnement" : "inaktive abonnement"} på denne
                        fakturaen
                    </i>
                </Text>
            )}
        </Box>
    );

    if (!showHeader) {
        return (
            <>
                {changeBlockedModal}
                {subscriptionContent}
            </>
        );
    }

    return (
        <>
            {changeBlockedModal}
            <Box className={`account-selector__account${isOpen ? " account-selector__account--open" : ""}`}>
                <Flex direction="row" align="center" gap="100">
                    {showRadio ? (
                        <RadioGroup.Item>
                            <RadioGroup.Trigger value={account.accountId} disabled={isDisabled} />
                            <RadioGroup.Label>
                                <Flex direction="column" gap="50">
                                    <Text as="span" variant={isSelected ? "paragraph-100-bold" : "paragraph-100"}>
                                        Fakturanr. {account.accountId} ({account.subscriptions.length} abo)
                                    </Text>
                                    {isFull && (
                                        <Text as="span" variant="additional-100" color="red">
                                            Fullt - kun endringer på eksisterende abonnement mulig
                                        </Text>
                                    )}
                                </Flex>
                            </RadioGroup.Label>
                        </RadioGroup.Item>
                    ) : (
                        <Text as="span" variant="paragraph-100-bold">
                            Fakturanr. {account.accountId} ({account.subscriptions.length} abo)
                        </Text>
                    )}
                    <Button
                        variant="text"
                        size="sm"
                        className="account-selector__account-toggle"
                        onClick={() => setIsOpen(!isOpen)}
                        aria-expanded={isOpen}
                        aria-label={`${isOpen ? "Lukk" : "Åpne"} fakturanr. ${account.accountId}`}
                    >
                        <Icon name={isOpen ? "chevron-down" : "chevron-right"} aria-hidden="true" focusable="false" />
                    </Button>
                </Flex>
                {isOpen && subscriptionContent}
            </Box>
        </>
    );
};

const AccountSelector: React.FC = () => {
    const dispatch = useDispatch();
    const selectedAccountId = useSelector((state: RootState) => getCustomerSelectedAccountId(state));
    const existingAccounts = useSelector((state: RootState) => getCustomerExistingAccounts(state));
    const activeSubscription = useSelector((state: RootState) => getActiveSubscription(state));
    const cartSubscriptions = useSelector(getSubscriptions);
    const [showNewAccountBlockedModal, setShowNewAccountBlockedModal] = useState(false);

    const hasExistingSubscriptionChangesInCart = useMemo(
        () => _values(cartSubscriptions).some((sub) => sub.phoneNumber?.check?.type === CUSTOMER_TYPE.EXISTING),
        [cartSubscriptions],
    );

    const handleSelectNewAccount = () => {
        if (hasExistingSubscriptionChangesInCart) {
            setShowNewAccountBlockedModal(true);
            return;
        }
        dispatch(setSelectedAccountId(NEW_ACCOUNT_VALUE));
    };

    const visibleAccounts = existingAccounts.filter(
        (acc) => acc.accountId !== NEW_ACCOUNT_VALUE && acc.subscriptions.length > 0,
    );
    const numberOfExistingAccounts = visibleAccounts.length;

    const isAccountFull = (account: CustomerExistingAccount) => {
        const existingPhoneNumbers = new Set(account.subscriptions.map((sub) => sub.phoneNumber));
        const newAdditionsForAccount = _values(cartSubscriptions).filter((sub) => {
            if (isNonMhsSubscription(sub.code)) return false;
            // Exclude the subscription currently being edited — it is already allocated and
            // should not push the account over the limit while the user configures it.
            if (sub.id === activeSubscription) return false;
            const phone = sub.phoneNumber?.selectedPhoneNumber;
            // Subscriptions without a phone number haven't been assigned to any account yet
            if (!phone) return false;
            // Exclude subscriptions that are changes to an existing one on this account
            if (existingPhoneNumbers.has(phone)) return false;
            // For phones with a known account (existing Telia numbers), use that account.
            // For new/port-in numbers without an account, fall back to the globally selected account.
            const targetAccountId = sub.phoneNumber?.accountId ?? selectedAccountId;
            return targetAccountId === account.accountId;
        }).length;

        return (
            account.subscriptions.filter(isCountableSubscription).length + newAdditionsForAccount >=
            MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS
        );
    };

    const isAccountFullFromExistingOnly = (account: CustomerExistingAccount) =>
        account.subscriptions.filter(isCountableSubscription).length >= MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS;

    const isAccountDisabled = (account: CustomerExistingAccount): boolean => {
        if (!isAccountFull(account)) return false;
        // Full account: still selectable if cart is empty or all cart subs belong to this account
        const accountPhoneNumbers = new Set(account.subscriptions.map((sub) => sub.phoneNumber));
        const cartSubsWithPhone = _values(cartSubscriptions).filter((sub) => !!sub.phoneNumber?.selectedPhoneNumber);
        if (cartSubsWithPhone.length === 0) return false;
        return !cartSubsWithPhone.every((sub) => accountPhoneNumbers.has(sub.phoneNumber!.selectedPhoneNumber));
    };

    const someAccountFullDueToCartAdditions = visibleAccounts.some(
        (acc) => !isAccountFullFromExistingOnly(acc) && isAccountFull(acc),
    );

    const singleAccount = numberOfExistingAccounts === 1 ? visibleAccounts[0] : undefined;
    const isSingleAccountFull = singleAccount ? isAccountFull(singleAccount) : false;

    useEffect(() => {
        // Don't auto-adjust the selected account while the user is actively editing a subscription.
        if (activeSubscription) return;
        if (singleAccount && !isSingleAccountFull && selectedAccountId !== singleAccount.accountId) {
            dispatch(setSelectedAccountId(singleAccount.accountId));
        }
    }, [singleAccount, isSingleAccountFull, selectedAccountId, activeSubscription, dispatch]);

    if (existingAccounts.length === 0) {
        return null;
    }

    const showNewAccountOption = !someAccountFullDueToCartAdditions;

    const selectedAccountForFull = visibleAccounts.find((acc) => acc.accountId === selectedAccountId);
    const isSelectedAccountFull = selectedAccountForFull ? isAccountFull(selectedAccountForFull) : false;
    const isSelectedAccountNaturallyFull = selectedAccountForFull
        ? isAccountFullFromExistingOnly(selectedAccountForFull)
        : false;

    if (singleAccount && !isSingleAccountFull) {
        return (
            <Box mb="200" mt="200">
                <AccountTree
                    account={singleAccount}
                    isFull={false}
                    isDisabled={false}
                    isSelected={true}
                    isSelectedAccountFull={false}
                    isSelectedAccountNaturallyFull={false}
                    showRadio={false}
                    showHeader={false}
                    defaultOpen={true}
                />
            </Box>
        );
    }

    return (
        <Box mb="200" mt="200">
            <ExistingSubscriptionNewAccountConflictModal
                open={showNewAccountBlockedModal}
                onClose={() => setShowNewAccountBlockedModal(false)}
                title="Kan ikke velge ny avtale"
                message="Ny avtale kan ikke velges når handlekurven inneholder endringer på eksisterende abonnement. Fjern endringene fra handlekurven for å velge ny avtale."
            />
            <RadioGroup
                value={selectedAccountId || ""}
                onValueChange={(value) => {
                    if (value === NEW_ACCOUNT_VALUE) {
                        handleSelectNewAccount();
                    } else {
                        dispatch(setSelectedAccountId(value));
                    }
                }}
            >
                <RadioGroup.GroupLabel>
                    Velg hvilken avtale abonnement skal knyttes til. <br />
                    Det er kun mulig å gjøre endringer på en faktura per ordre.
                </RadioGroup.GroupLabel>
                {existingAccounts.map((account) => (
                    <AccountTree
                        key={account.accountId}
                        account={account}
                        isFull={isAccountFull(account)}
                        isDisabled={isAccountDisabled(account)}
                        isSelected={account.accountId === selectedAccountId}
                        isSelectedAccountFull={isSelectedAccountFull}
                        isSelectedAccountNaturallyFull={isSelectedAccountNaturallyFull}
                        showRadio={true}
                        defaultOpen={numberOfExistingAccounts === 1}
                    />
                ))}
                {showNewAccountOption && (
                    <Box className="account-selector__account" mt="100">
                        <RadioGroup.Item>
                            <RadioGroup.Trigger value={NEW_ACCOUNT_VALUE} />
                            <RadioGroup.Label>
                                <Text
                                    as="span"
                                    variant={
                                        NEW_ACCOUNT_VALUE === selectedAccountId ? "paragraph-100-bold" : "paragraph-100"
                                    }
                                >
                                    Ny avtale
                                </Text>
                            </RadioGroup.Label>
                        </RadioGroup.Item>
                    </Box>
                )}
            </RadioGroup>
        </Box>
    );
};

export default AccountSelector;
